/**
 * Enquête n°17 — Adapté de l'Étude de Cas CCS n°11
 * (Sensibiliser sur les risques de la circulation en groupe des cyclomoteurs.)
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const groupeCycloEnquete = {
  slug: 'circulation-groupe-cyclo',
  themeCode: 'M',
  issueNumber: 17,
  totalIssues: 17,
  series: 'enquetes',
  category: 'Enquête sensibilisation & comportemental',
  shortTitle: 'Circulation en groupe',
  title: 'En meute à cyclo : pourquoi ton cerveau décroche',
  kicker: 'Enquête n°17 · Série « Témoignages & Sécurité »',
  subtitle:
    "L'effet de meute de Le Bon (1895), redécouvert au 21ᵉ siècle par les neurosciences : en groupe, ta responsabilité personnelle baisse de 30 à 50 %. 60 % des accidents cyclo des jeunes surviennent à plusieurs. Voici les 5 règles d'or de la conduite en groupe.",
  heroImage: '/img/magazines/environnement.jpg',
  heroImageAlt: "Deux motards en virage sur une route sinueuse — illustration de la circulation en groupe",
  readTimeMin: 9,
  seoDescription:
    "Enquête sensibilisation sur la conduite en groupe à cyclo pour 14-16 ans : effet de meute, formation en quinconce, rôles leader/fermeur, refus de la pression des pairs.",
  seoKeywords:
    'circulation en groupe, effet de meute, Le Bon, quinconce, leader fermeur, pression pairs, conduite groupe cyclo',

  edito: {
    title: 'En groupe, je reste maître de ma trajectoire',
    body: "Tu rejoins tes potes à cyclo pour aller au foot, au ciné, à la fête. Vous êtes 3, 4, 6 cyclos. Et soudain, sans même le décider, tu roules plus vite, tu prends plus de risques, tu te faufiles entre les voitures, tu doubles dans des situations où seul tu n'aurais jamais tenté. <strong>C'est l'effet de meute</strong>. Théorisé par Gustave Le Bon en 1895, confirmé par les neurosciences modernes (études Cialdini, Asch) : en groupe, ta responsabilité personnelle baisse de 30 à 50 %, ton seuil de prise de risque s'élève. Cette enquête adapte l'étude de cas CCS n°11 — sensibilisation à la circulation en groupe — et te donne les 5 règles d'or pour profiter de la balade collective sans terminer aux urgences.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les 4 effets du groupe sur ton cerveau',
      paragraphs: [
        "<strong>1. La désindividuation</strong> (Le Bon, 1895). En groupe, ton « moi » s'efface partiellement au profit du « nous ». Tu te sens moins responsable de tes actes, plus protégé par le nombre. C'est la base psychologique des comportements collectifs anormaux (émeutes, courses, vandalisme).",
        "<strong>2. La surconfiance par résonance</strong>. Voir tes potes réussir un dépassement risqué te fait croire que TU vas réussir le même. Sauf que ton niveau, ta moto, ta concentration ne sont pas identiques. Statistique : 70 % des chutes solo « en groupe » arrivent à celui qui n'a PAS tenté le truc en premier mais qui suit.",
        "<strong>3. La pression sociale conformiste</strong> (Asch, 1951). Tu te plies au comportement du groupe pour ne pas être exclu. Si 3 potes accélèrent, tu accélères. Si 4 potes débordent, tu débordes. Même si tu sais que c'est dangereux. <strong>Ton intelligence rationnelle est court-circuitée par ton circuit social.</strong>",
        "<strong>4. La prise de risque compétitive</strong>. Ado-mode activé : « je suis aussi fort que toi, regarde ça ». Une course informelle se crée, même sans annonce. À cyclo limité à 45 km/h, la différence se fait à l'engagement dans les courbes, aux freinages tardés, aux dépassements limites. <strong>Le dernier qui doute perd — ou se tue.</strong>",
      ],
      keyPoints: [
        '1. Désindividuation : moi → nous',
        '2. Surconfiance par résonance',
        '3. Conformisme (Asch)',
        '4. Prise de risque compétitive',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "<strong>La formation en quinconce</strong> (recommandée par la FFM et la Sécurité Routière) est la façon la plus sûre de rouler en groupe à 2 roues. Le principe : chaque conducteur se positionne décalé latéralement par rapport à celui devant. Le 1ʳ à gauche de la voie, le 2° à droite, le 3° à gauche, etc. Avantages : 1) <strong>visibilité frontale et latérale accrue</strong> (chaque rider voit la route au-delà du précédent), 2) <strong>marges de manœuvre préservées</strong> (chacun a 1 mètre latteral pour éviter), 3) <strong>lisibilité des panneaux et obstacles</strong>. À éviter absolument : la file unique serrée (effet domino en cas de freinage) et le côte-à-côte (impossibilité d'éviter, double impact).",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les 5 règles d’or de la balade en groupe',
      paragraphs: [
        "<strong>1. Briefing AVANT le départ</strong>. Itinéraire connu de tous, points de regroupement (stations-service, parking de centre commercial), allure adaptée au plus faible, signaux convenus (poing serré = ralentir, main en avant = obstacle).",
        "<strong>2. Leader expérimenté, fermeur calme</strong>. Le leader donne le rythme (jamais à fond), signale les obstacles. Le fermeur ferme le groupe, signale les dépassements arrière, ramasse les décrochés. <strong>Les débutants au milieu</strong>, jamais en tête ni en queue.",
        "<strong>3. Formation en quinconce à 12 m</strong>. Distance entre chaque cyclo : 12 mètres minimum (1 seconde à 45 km/h). Latéralement : décalé droite/gauche. Ne JAMAIS rouler en file serrée (< 5 m).",
        "<strong>4. Tu suis ton allure, pas celle du groupe</strong>. Si le leader part trop vite, tu ne suis pas. Tu rejoindras le groupe au point de regroupement convenu. <strong>Suivre un copain plus rapide = suivre ses erreurs</strong>, multipliciées par 1 seconde de réaction.",
        "<strong>5. Refus de la pression : la phrase magique</strong>. Si quelqu'un te pousse à prendre un risque (« vas-y, tente »), la formule est : <strong>« Pas en groupe, c'est ma règle »</strong>. Personne n'argumente contre une règle personnelle. Si la pression persiste, tu décroches du groupe (rejoint au point de RV). Mieux vaut perdre une réputé que perdre une jambe.",
      ],
      keyPoints: [
        '1. Briefing pré-départ',
        '2. Leader + fermeur, débutants au milieu',
        '3. Quinconce, 12 m d\'écart',
        '4. Ta vitesse, pas celle du groupe',
        '5. Phrase magique anti-pression',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Mes potes savent, je les suis. » <strong>À éviter absolument.</strong> En groupe de cyclos jeunes, la <strong>connaissance de la route</strong> et la <strong>maîtrise technique</strong> sont rarement réparties uniformément. Le « leader » autoproclamé est souvent celui qui parle le plus fort, pas celui qui a le plus d'expérience. Résultat : il s'engage dans un virage à 70 km/h (en cyclo débridé), il passe, tu suis à 65 km/h, tu ne passes pas. Et tu ne lui en veux même pas — c'est toi qui as choisi de le suivre. <strong>La règle absolue : tu régles ta vitesse sur TA capacité à t'arrêter dans la distance que tu vois</strong>, pas sur celle du copain devant. Si le copain s'écarte, tu restes en sécurité.",
    },
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Pour l'étude de cas n°11, je projette toujours <strong>une vidéo YouTube authentique</strong> : 4 cyclos qui partent ensemble un samedi après-midi, 3 rentrent, 1 est aux urgences. J'arrête la vidéo avant l'accident et je demande aux 3 élèves : « À quel moment vous auriez dû décrocher ? ». Réponses : « au feu rouge brulé », « quand il a doublé par la droite ». Bonne réponses. <strong>Conseil aux parents : assurez-vous que votre ado a un point de regroupement convenu (« on s'attend au Mac do »), pas une « balade libre » sans cadre.</strong> Et faites-lui mémoriser la phrase « Pas en groupe, c'est ma règle » avant son départ. Ça sauve des vies. »",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '60 %',
    middle: 'des accidents cyclo',
    label: "des moins de 18 ans surviennent dans un contexte de circulation en groupe de 2+ cyclos. L'effet de meute multiplie par 2 à 3 la prise de risque individuelle.",
    source: 'ONISR — Accidentalité 2RM jeunes en groupe',
    accent: '#db2777',
  },

  glossary: [
    { term: 'Effet de meute (Le Bon)', def: 'Théorie 1895 : en groupe, responsabilité personnelle baisse et prise de risque augmente. Base de la psychologie des foules.' },
    { term: 'Désindividuation', def: 'Effacement partiel du « moi » au profit du « nous » en groupe. Diminue le sentiment de responsabilité individuelle.' },
    { term: 'Conformisme (Asch)', def: 'Expériences 1951 : un individu se plie au comportement du groupe même contre sa propre conviction, pour ne pas être exclu.' },
    { term: 'Formation en quinconce', def: 'Disposition recommandée (FFM, Sécurité Routière) : chaque rider décalé latéralement par rapport au précédent, 12 m d\'écart.' },
    { term: 'Leader / Fermeur', def: 'Rôles dans un groupe 2RM. Leader expérimenté donne le rythme et signale les obstacles. Fermeur ferme le groupe, signale les dépassements arrière.' },
    { term: 'Phrase magique', def: 'Outil ECSR de refus social non-conflictuel : « Pas en groupe, c\'est ma règle ». Ne se discute pas.' },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "L'étude de cas n°11 traite de la sensibilisation aux risques spécifiques à la circulation en groupe des cyclomoteurs. Public : 3 candidats AM (14-16 ans).",
    points: [
      'Public-cible : 3 candidats permis AM (14-16 ans) avec quelques heures de pratique cyclo.',
      'Type d\'intervention : sensibiliser (changement comportemental visé).',
      'Durée : 1 heure — vidéo + témoignage écrit d\'un parent + apports théoriques + jeu de rôle sur table + QCM.',
      'Méthode : vidéo réelle d\'accident en groupe, expositif sur la psycho sociale (Le Bon, Asch), simulation avec figurines.',
      "Résultat attendu : l'élève identifie les 4 effets du groupe, connaît la quinconce, sait refuser une situation à risque imposée par le groupe.",
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°11 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-11',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur la conduite en groupe : effets psycho-sociaux, formation, rôles, refus de la pression. Réussite minimum : 8/10.",
    themeCode: 'M',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: null,
};

export default groupeCycloEnquete;
